import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { X, Zap, Clock, ArrowRight } from 'lucide-react';
import { Button } from './ui/button';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Animation CSS classes
const ANIMATIONS = {
  pulse: 'animate-pulse',
  shake: 'animate-bounce',
  glow: 'shadow-[0_0_30px_rgba(249,115,22,0.5)]',
  bounce: 'animate-bounce'
};

// Valeurs par défaut si l'API ne répond pas
const DEFAULT_SETTINGS = {
  enabled: true,
  show_as_popup: false,
  show_on_location_page: true,
  image: "",
  title: "🔥 OFFRE FLASH",
  subtitle: "Pack Barbe à Papa COMPLET",
  description: "Machine PRO + 50 portions + livraison OFFERTE",
  original_price: "120€",
  promo_price: "89€",
  discount_percent: "-25%",
  validity: "Offre limitée - Réservez vite !",
  cta_text: "J'en profite !",
  link: "/contact",
  badge_text: "PROMO",
  animation_style: "pulse"
};

// Banner component for Location page
export function FlashLocationBanner() {
  const [settings, setSettings] = useState(null);
  const [dismissed, setDismissed] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    loadSettings();
  }, []);

  const loadSettings = async () => {
    try {
      setLoading(true);
      const res = await axios.get(`${API}/flash-location`);
      setSettings(res.data);
      setError(null);
    } catch (err) {
      console.error('Erreur chargement offre flash:', err);
      // Utiliser les valeurs par défaut si l'API échoue
      setSettings(DEFAULT_SETTINGS);
      setError(null); // On ne considère pas ça comme une erreur bloquante
    } finally {
      setLoading(false);
    }
  };

  // Don't show anything while loading or if there's an error
  if (loading || error || !settings || !settings.enabled || !settings.show_on_location_page || dismissed) {
    return null;
  }

  return (
    <div className={`relative overflow-hidden rounded-2xl bg-gradient-to-r from-orange-500 via-red-500 to-pink-500 p-1 mb-8 ${ANIMATIONS[settings.animation_style] || ''}`}>
      <div className="bg-white dark:bg-gray-900 rounded-xl p-6 md:p-8 relative">
        {/* Close button */}
        <button 
          onClick={() => setDismissed(true)}
          className="absolute top-2 right-2 p-1 rounded-full hover:bg-gray-100 transition-colors"
        >
          <X className="w-5 h-5 text-gray-400" />
        </button>

        {/* Badge promo */}
        <div className="absolute -top-1 -left-1 md:top-4 md:left-4">
          <div className="bg-gradient-to-r from-orange-500 to-red-500 text-white text-sm md:text-lg font-bold px-3 py-1 rounded-full shadow-lg flex items-center gap-1">
            <Zap className="w-4 h-4" />
            {settings.discount_percent}
          </div>
        </div>

        {/* Badge texte */}
        <div className="absolute -top-2 -right-2 md:top-2 md:right-12 transform rotate-12">
          <div className="bg-red-500 text-white text-xs font-bold px-3 py-1 rounded-full shadow-lg animate-pulse">
            {settings.badge_text}
          </div>
        </div>

        <div className="flex flex-col md:flex-row items-center gap-6 pt-6 md:pt-0">
          {/* Image si présente */}
          {settings.image && (
            <div className="w-32 h-32 md:w-40 md:h-40 flex-shrink-0">
              <img 
                src={settings.image} 
                alt={settings.subtitle}
                className="w-full h-full object-contain"
              />
            </div>
          )}

          {/* Contenu */}
          <div className="flex-1 text-center md:text-left">
            <h3 className="text-2xl md:text-3xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-orange-500 to-red-500">
              {settings.title}
            </h3>
            <p className="text-xl md:text-2xl font-semibold text-gray-800 mt-1">
              {settings.subtitle}
            </p>
            <p className="text-gray-600 mt-2 text-sm md:text-base">
              {settings.description}
            </p>
            
            <div className="flex items-center justify-center md:justify-start gap-2 mt-2 text-orange-600">
              <Clock className="w-4 h-4" />
              <span className="text-sm font-medium">{settings.validity}</span>
            </div>
          </div>

          {/* Prix & CTA */}
          <div className="flex flex-col items-center gap-3">
            <div className="text-center">
              <span className="text-gray-400 line-through text-lg block">
                {settings.original_price}
              </span>
              <span className="text-4xl font-bold text-red-500">
                {settings.promo_price}
              </span>
            </div>
            
            <Link to={settings.link}>
              <Button className="bg-gradient-to-r from-orange-500 to-red-500 hover:from-orange-600 hover:to-red-600 text-white font-bold px-6 py-3 rounded-full shadow-lg transform hover:scale-105 transition-all">
                {settings.cta_text}
                <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
}

// Popup component for site-wide display
export function FlashLocationPopup() {
  const [settings, setSettings] = useState(null);
  const [isOpen, setIsOpen] = useState(false);

  useEffect(() => {
    loadSettings();
  }, []);

  useEffect(() => {
    if (settings?.enabled && settings?.show_as_popup) {
      // Check if user already dismissed today
      const dismissed = sessionStorage.getItem('flash_location_dismissed');
      if (!dismissed) {
        // Show popup after 3 seconds
        const timer = setTimeout(() => setIsOpen(true), 3000);
        return () => clearTimeout(timer);
      }
    }
  }, [settings]);

  const loadSettings = async () => {
    try {
      const res = await axios.get(`${API}/flash-location`);
      setSettings(res.data);
    } catch (error) {
      console.error('Erreur chargement offre flash:', error);
    }
  };

  const handleClose = () => {
    setIsOpen(false);
    sessionStorage.setItem('flash_location_dismissed', 'true');
  };

  if (!settings || !settings.enabled || !settings.show_as_popup || !isOpen) {
    return null;
  }

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center p-4">
      {/* Backdrop */}
      <div 
        className="absolute inset-0 bg-black/60 backdrop-blur-sm"
        onClick={handleClose}
      />
      
      {/* Modal */}
      <div className={`relative w-full max-w-lg transform transition-all ${ANIMATIONS[settings.animation_style] || ''}`}>
        <div className="relative overflow-hidden rounded-3xl bg-gradient-to-r from-orange-500 via-red-500 to-pink-500 p-1 shadow-2xl">
          <div className="bg-white rounded-2xl p-6 md:p-8 relative">
            {/* Close button */}
            <button 
              onClick={handleClose}
              className="absolute top-3 right-3 p-2 rounded-full bg-gray-100 hover:bg-gray-200 transition-colors z-10"
            >
              <X className="w-5 h-5 text-gray-600" />
            </button>

            {/* Badge discount */}
            <div className="absolute top-4 left-4">
              <div className="bg-gradient-to-r from-orange-500 to-red-500 text-white text-xl font-bold px-4 py-2 rounded-full shadow-lg flex items-center gap-2 animate-pulse">
                <Zap className="w-5 h-5" />
                {settings.discount_percent}
              </div>
            </div>

            {/* Badge promo */}
            <div className="absolute -top-2 -right-2 transform rotate-12">
              <div className="bg-red-500 text-white text-sm font-bold px-4 py-1 rounded-full shadow-lg">
                {settings.badge_text}
              </div>
            </div>

            {/* Content */}
            <div className="text-center pt-12">
              {settings.image && (
                <img 
                  src={settings.image} 
                  alt={settings.subtitle}
                  className="w-32 h-32 object-contain mx-auto mb-4"
                />
              )}

              <h3 className="text-3xl md:text-4xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-orange-500 to-red-500">
                {settings.title}
              </h3>
              
              <p className="text-xl md:text-2xl font-semibold text-gray-800 mt-2">
                {settings.subtitle}
              </p>
              
              <p className="text-gray-600 mt-3 text-base md:text-lg">
                {settings.description}
              </p>

              {/* Prix */}
              <div className="flex items-center justify-center gap-4 mt-6">
                <span className="text-gray-400 line-through text-xl">
                  {settings.original_price}
                </span>
                <span className="text-5xl font-bold text-red-500">
                  {settings.promo_price}
                </span>
              </div>

              {/* Validité */}
              <div className="flex items-center justify-center gap-2 mt-3 text-orange-600">
                <Clock className="w-5 h-5 animate-pulse" />
                <span className="font-medium">{settings.validity}</span>
              </div>

              {/* CTA */}
              <Link to={settings.link} onClick={handleClose}>
                <Button className="mt-6 bg-gradient-to-r from-orange-500 to-red-500 hover:from-orange-600 hover:to-red-600 text-white font-bold text-lg px-10 py-4 rounded-full shadow-xl transform hover:scale-105 transition-all">
                  {settings.cta_text}
                  <ArrowRight className="w-5 h-5 ml-2" />
                </Button>
              </Link>

              <p className="text-xs text-gray-400 mt-4">
                Cliquez en dehors pour fermer
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default FlashLocationBanner;
